import React from "react";

const SUMMARY_ITEMS = Object.freeze([
  ["represented", "Destinations"],
  ["connected", "Connection signals"],
  ["reporting", "Reporting ready"],
  ["audienceSpecific", "Platform synthesis"],
  ["stale", "Stale sources"],
  ["missing", "Evidence gaps"],
]);

function StatusPill({ tone, children }) {
  return (
    <span className={`omni-intelligence-status omni-intelligence-status--${tone}`}>
      {children}
    </span>
  );
}

function PlatformHero({ platform }) {
  const [imageFailed, setImageFailed] = React.useState(false);
  const initials = String(platform.displayLabel || platform.label || platform.key)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
  const showImage = Boolean(platform.image) && !imageFailed;

  return (
    <span
      className={`omni-intelligence-platform-hero${
        showImage ? "" : " omni-intelligence-platform-hero--fallback"
      }`}
      aria-hidden="true"
    >
      {showImage ? (
        <img
          src={platform.image}
          alt=""
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span>{initials}</span>
      )}
    </span>
  );
}

export default function OmniIntelligencePanel({
  model,
  onNavigate,
  loading = false,
  error = "",
}) {
  if (!model) return null;

  return (
    <section
      className="omni-intelligence-panel"
      aria-labelledby="omni-intelligence-title"
      aria-describedby="omni-intelligence-disclosure"
      data-testid="omni-intelligence-panel"
    >
      <header className="omni-intelligence-header">
        <div>
          <p className="omni-intelligence-kicker">Portfolio intelligence</p>
          <h2 id="omni-intelligence-title">Omni Intelligence</h2>
          <p className="omni-intelligence-subtitle">
            Governed analysis across every connected social platform and omni channel.
          </p>
        </div>
        <span className="omni-intelligence-readonly">READ-ONLY</span>
      </header>

      <div
        className="omni-intelligence-live-state"
        aria-live="polite"
        aria-busy={loading}
      >
        {loading ? "Resolving governed portfolio evidence…" : null}
        {!loading && error ? error : null}
        {!loading && !error ? model.analysis.confidence : null}
      </div>

      <div className="omni-intelligence-summary" aria-label="Portfolio coverage summary">
        {SUMMARY_ITEMS.map(([key, label]) => (
          <div className="omni-intelligence-summary-card" key={key}>
            <strong>{model.summary[key]}</strong>
            <span>{label}</span>
          </div>
        ))}
      </div>

      <div className="omni-intelligence-analysis">
        <article>
          <h3>Observed</h3>
          <p>{model.analysis.observed}</p>
        </article>
        <article>
          <h3>Interpretation</h3>
          <p>{model.analysis.interpretation}</p>
        </article>
        <article>
          <h3>Recommended investigation</h3>
          <p>{model.analysis.recommendedInvestigation}</p>
        </article>
      </div>

      <div className="omni-intelligence-coverage" role="list" aria-label="All platform coverage">
        {model.platforms.map((platform) => (
          <article
            className="omni-intelligence-platform"
            key={platform.key}
            role="listitem"
            data-platform-key={platform.key}
          >
            <div className="omni-intelligence-platform-heading">
              <div className="omni-intelligence-platform-identity">
                <PlatformHero
                  key={platform.image || `${platform.key}-fallback`}
                  platform={platform}
                />
                <div className="omni-intelligence-platform-heading-copy">
                  <h3>{platform.displayLabel}</h3>
                  <span>{platform.group}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onNavigate(platform.route)}
                aria-label={`View ${platform.label} analytics`}
              >
                View analytics
              </button>
            </div>
            <div className="omni-intelligence-platform-statuses">
              <StatusPill tone={platform.connected ? "ready" : "missing"}>
                {platform.connectionLabel}
              </StatusPill>
              <StatusPill tone={platform.reporting.state}>
                {platform.reporting.label}
              </StatusPill>
              <StatusPill tone={platform.audience.state}>
                {platform.audience.label}
              </StatusPill>
              <StatusPill tone={platform.freshness.state}>
                {platform.freshness.label}
              </StatusPill>
            </div>
            {platform.reporting.primaryMetric ? (
              <p className="omni-intelligence-platform-metric">
                <strong>{platform.reporting.primaryMetric.label}</strong>{" "}
                {platform.reporting.primaryMetric.value}
              </p>
            ) : null}
            <p className="omni-intelligence-platform-limitation">
              {platform.limitation}
            </p>
            {platform.evidenceState ? (
              <p className="omni-intelligence-platform-evidence">
                Evidence: {platform.evidenceState}
              </p>
            ) : null}
          </article>
        ))}
      </div>

      <p id="omni-intelligence-disclosure" className="omni-intelligence-disclosure">
        {model.disclosure}
      </p>
    </section>
  );
}
